/**
 * Understanding Your Pay Page
 * Educational section with service transparency ledgers
 * 
 * Intro card: "Where a service dollar goes"
 * "Every treatment carries the whole studio on its back — here's the honest math."
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ServiceTransparencyModal from '@/components/ServiceTransparencyModal';
import { CommissionChart } from '@/components/CommissionChart';
import api from '@/lib/api';
import { CuteLoader } from "@/components/CuteLoader";

export default function UnderstandingYourPayPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchServices = useCallback(async () => {
    try {
      const response = await api.get('/services/list');
      setServices(response.data.services_by_type);
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleServiceClick = (serviceName) => {
    setSelectedService(serviceName);
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="border-b border-border bg-card px-4 py-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Understanding Your Pay</h1>
        <p className="text-sm text-muted-foreground mt-1">How your earnings work</p>
      </div>

      <div className="p-4 space-y-6">
        {/* Intro Card */}
        <Card className="bg-gradient-to-br from-[#3d2817] to-[#2a1810] text-white">
          <CardContent className="p-6">
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-semibold mb-3">
              Where a service dollar goes
            </h3>
            <p className="text-[#f5f5f0] leading-relaxed">
              Every treatment carries the whole studio on its back — here's the honest math.
            </p>
          </CardContent>
        </Card>

        {/* Gross vs Take-home */}
        <Card data-testid="gross-vs-takehome-card">
          <CardHeader>
            <CardTitle>Gross vs. take-home</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              Your <strong className="text-foreground">gross</strong> is everything you earned this period —
              hourly, commission, and tips. Your <strong className="text-foreground">take-home</strong> is what
              actually lands in your account after required withholdings.
            </p>
            <div className="space-y-2">
              <div><span className="text-foreground font-medium">Federal income tax</span> — set by the W-4 you filled out; more dependents means less withheld.</div>
              <div><span className="text-foreground font-medium">Social Security &amp; Medicare</span> — a fixed slice everyone pays; it funds your future benefits.</div>
              <div><span className="text-foreground font-medium">State &amp; local tax</span> — depends on where you live and work.</div>
            </div>
            <p>
              So two people with the same gross can take home different amounts — it comes down to your W-4 and
              where you live. Your payday recap shows your exact numbers whenever the payroll register is in.
            </p>
          </CardContent>
        </Card>
        {loading ? (
          <CuteLoader />
        ) : (
          <>
            {Object.entries(services).map(([type, serviceList]) => (
              <Card key={type}>
                <CardHeader>
                  <CardTitle className="capitalize">
                    {type === 'hydrafacial' ? 'HydraFacial Treatments' : type.replace('_', ' ')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {serviceList.map((service) => (
                    <button
                      key={service.service_name}
                      onClick={() => handleServiceClick(service.service_name)}
                      className="w-full flex justify-between items-center p-4 hover:bg-muted rounded-lg transition-colors text-left"
                    >
                      <div>
                        <div className="font-medium text-foreground">{service.service_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {service.duration_minutes} minutes
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#d4a574]">
                          ${service.price.toFixed(2)}
                        </div>
                        <div className="text-xs text-muted-foreground">View breakdown</div>
                      </div>
                    </button>
                  ))}
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {/* Educational Note */}
        <Card>
          <CardContent className="p-6">
            <h4 className="font-semibold mb-2 text-foreground">Your Earnings Structure</h4>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              Your compensation has two parts: your hourly rate for your time, plus commission on services you perform based on your tier.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Tips are 100% yours on top of both. The transparency ledgers show where each service dollar goes — from consumables to equipment to studio overhead to your earnings.
            </p>
          </CardContent>
        </Card>

        <CommissionChart />
      </div>

      {/* Service Transparency Modal */}
      <ServiceTransparencyModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        serviceName={selectedService}
      />
    </div>
  );
}
